'use client';

import { clientFetch } from '@/lib/api/clientFetch';
import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Offcanvas, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import './buyBoxBell.scss';

function readMessage(messages, path, fallback = '') {
  const resolved = path
    .split('.')
    .reduce((current, segment) => current?.[segment], messages);

  return typeof resolved === 'string' ? resolved : fallback;
}

function resolveBellMessage(messages, key, fallback = '') {
  const candidates = {
    callbackTitle: ['buyBox.callbackTitle', 'callBack.title'],
    callbackCopy: ['buyBox.callbackCopy', 'callBack.sendMicrocopy'],
    callbackRequired: ['buyBox.callbackRequired', 'callBack.required'],
    callbackSuccess: ['buyBox.callbackSuccess', 'callBack.success'],
    callbackError: ['buyBox.callbackError', 'callBack.error'],
    callbackSending: ['buyBox.callbackSending', 'sending'],
    requestCallback: [
      'buyBox.requestCallback',
      'callBack.send',
      'callBack.button',
    ],
    name: ['buyBox.name', 'name'],
    phone: ['buyBox.phone', 'phone'],
  };

  for (const path of candidates[key] || []) {
    const value = readMessage(messages, path, '');
    if (value) {
      return value;
    }
  }

  return fallback;
}

export default function BuyBoxBell({
  currentUser,
  messages = {},
  product,
  qty,
  items = null,
  copy,
  requiredMessage,
  successMessage,
  errorMessage,
  triggerClassName = '',
  offcanvasId = 'buybox-callback-offcanvas',
}) {
  const triggerRef = useRef(null);
  const bellAudioRef = useRef(null);
  const ringingTimeoutRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [isRinging, setIsRinging] = useState(false);

  useEffect(() => {
    return () => {
      window.clearTimeout(ringingTimeoutRef.current);

      if (bellAudioRef.current) {
        try {
          bellAudioRef.current.pause();
          bellAudioRef.current.currentTime = 0;
        } catch {}
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setName(currentUser?.name || '');
    setPhone(currentUser?.phone || '');
  }, [currentUser?.name, currentUser?.phone, isOpen]);

  const closeBell = () => {
    setIsOpen(false);
    setIsRinging(false);

    if (bellAudioRef.current) {
      try {
        bellAudioRef.current.pause();
        bellAudioRef.current.currentTime = 0;
      } catch {}
    }

    window.setTimeout(() => {
      triggerRef.current?.focus?.();
    }, 0);
  };

  const callbackItems = Array.isArray(items)
    ? items
    : product?.productNumber
      ? [
          {
            productNumber: product.productNumber,
            qty,
          },
        ]
      : [];

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();

    if (!trimmedName || !trimmedPhone) {
      const message =
        requiredMessage ||
        resolveBellMessage(
          messages,
          'callbackRequired',
          'Please provide your name and phone number.',
        );

      setFeedback({ tone: 'error', message });
      toast.error(message);
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);

    try {
      await clientFetch('/api/call-requests', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          name: trimmedName,
          phone: trimmedPhone,
          userNumber: currentUser?.userNumber || null,
          items: callbackItems.map((item) => ({
            productNumber: item?.productNumber,
            qty: Number(item?.qty || 1),
            isGift: Boolean(item?.isGift),
          })),
        }),
      });

      const nextSuccessMessage =
        successMessage ||
        resolveBellMessage(
          messages,
          'callbackSuccess',
          'Callback request sent.',
        );

      setFeedback({ tone: 'success', message: nextSuccessMessage });
      toast.success(nextSuccessMessage);

      window.setTimeout(() => {
        closeBell();
      }, 320);
    } catch (error) {
      const nextErrorMessage =
        error?.data?.message ||
        errorMessage ||
        resolveBellMessage(
          messages,
          'callbackError',
          'Unable to send callback request.',
        );

      setFeedback({ tone: 'error', message: nextErrorMessage });
      toast.error(nextErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpen = () => {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    setFeedback(null);

    if (!prefersReducedMotion) {
      window.clearTimeout(ringingTimeoutRef.current);
      setIsRinging(true);

      try {
        if (bellAudioRef.current) {
          bellAudioRef.current.currentTime = 0;
          bellAudioRef.current.play?.();
        }
      } catch {}

      ringingTimeoutRef.current = window.setTimeout(() => {
        setIsRinging(false);
      }, 3000);
    }

    setIsOpen(true);
  };

  const buttonLabel = resolveBellMessage(
    messages,
    'requestCallback',
    'Request callback',
  );
  const callbackTitle = resolveBellMessage(
    messages,
    'callbackTitle',
    'Request a callback',
  );
  const callbackCopy =
    copy ||
    resolveBellMessage(
      messages,
      'callbackCopy',
      'Leave your phone number and we will contact you shortly.',
    );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={['buybox__bell-trigger', triggerClassName]
          .filter(Boolean)
          .join(' ')}
        aria-label={buttonLabel}
        aria-controls={offcanvasId}
        aria-expanded={isOpen}
        onClick={handleOpen}
      >
        <span
          aria-hidden="true"
          className={`buybox__bell-icon${isRinging ? ' is-ringing' : ''}`}
        >
          🔔
        </span>
        <span className="visually-hidden">{buttonLabel}</span>
      </button>

      <Offcanvas
        id={offcanvasId}
        show={isOpen}
        onHide={closeBell}
        placement="end"
        className="buybox__offcanvas"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <span
              aria-hidden="true"
              className={`buybox__bell-icon${isRinging ? ' is-ringing' : ''}`}
            >
              🔔
            </span>{' '}
            {callbackTitle}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <p className="buybox__offcanvas-copy">{callbackCopy}</p>

          {feedback ? (
            <Alert variant={feedback.tone === 'error' ? 'danger' : 'success'}>
              {feedback.message}
            </Alert>
          ) : null}

          <Form onSubmit={handleSubmit} className="buybox__callback-form">
            <Form.Group
              className="buybox__callback-field"
              controlId={`${offcanvasId}-name`}
            >
              <Form.Label>
                {resolveBellMessage(messages, 'name', 'Name')}
              </Form.Label>
              <Form.Control
                type="text"
                value={name}
                autoComplete="name"
                onChange={(event) => setName(event.target.value)}
              />
            </Form.Group>

            <Form.Group
              className="buybox__callback-field"
              controlId={`${offcanvasId}-phone`}
            >
              <Form.Label>
                {resolveBellMessage(messages, 'phone', 'Phone')}
              </Form.Label>
              <Form.Control
                type="tel"
                value={phone}
                autoComplete="tel"
                inputMode="tel"
                onChange={(event) => setPhone(event.target.value)}
              />
            </Form.Group>

            <Button
              type="submit"
              className="buybox__callback-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  {resolveBellMessage(
                    messages,
                    'callbackSending',
                    'Sending...',
                  )}
                </>
              ) : (
                buttonLabel
              )}
            </Button>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>

      <audio ref={bellAudioRef} className="d-none" aria-hidden preload="none">
        <source src="/hand-bell.mp3" type="audio/mpeg" />
      </audio>
    </>
  );
}
